
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Mic, MicOff, Volume2, AlertTriangle, ArrowLeft } from 'lucide-react';

function MKVoice() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isActive, setIsActive] = useState(false);
  const [error, setError] = useState(null);
  const [volume, setVolume] = useState(0);
  
  const audioContextRef = useRef(null);
  const sourceNodeRef = useRef(null);
  const analyzerRef = useRef(null);
  const animationRef = useRef(null);
  const streamRef = useRef(null);
  const canvasRef = useRef(null);

  const isPro = user?.es_pro || user?.role === 'entrenador';

  useEffect(() => {
    if (!isPro) {
      navigate('/');
    }
    
    return () => {
      stopAudio();
    };
  }, [isPro, navigate]);

  const startAudio = async () => {
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
            echoCancellation: false,
            noiseSuppression: false,
            autoGainControl: false,
            latency: 0
        } 
      });
      
      streamRef.current = stream;
      
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      const ctx = new AudioContext();
      audioContextRef.current = ctx;

      const source = ctx.createMediaStreamSource(stream);
      sourceNodeRef.current = source;
      
      // Analyzer for visualization
      const analyzer = ctx.createAnalyser();
      analyzer.fftSize = 256;
      analyzerRef.current = analyzer;
      
      // Connect source -> analyzer -> destination (speakers)
      source.connect(analyzer);
      analyzer.connect(ctx.destination);
      
      setIsActive(true);
      drawVisualizer();
      
    } catch (err) {
      console.error("Error accessing microphone:", err);
      setError("No se pudo acceder al micrófono. Asegúrate de dar permisos y de estar en HTTPS.");
    }
      // Check if protocol is HTTP (not secure) and not localhost
      if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost') {
        setError("⚠️ Estás accediendo por HTTP inseguro. El navegador bloqueará el micrófono. Necesitas configurar HTTPS (SSL) en tu NAS o servidor.");
        return;
      }
  };

  const stopAudio = () => {
    if (sourceNodeRef.current) {
      sourceNodeRef.current.disconnect();
    }
    if (analyzerRef.current) {
      analyzerRef.current.disconnect();
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
    
    setIsActive(false);
    setVolume(0);
  };

  const toggleAudio = () => {
    if (isActive) {
      stopAudio();
    } else {
      startAudio();
    }
  };

  const drawVisualizer = () => {
    if (!analyzerRef.current || !canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const bufferLength = analyzerRef.current.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    
    const draw = () => {
      if (!isActive) return;
      
      animationRef.current = requestAnimationFrame(draw);
      analyzerRef.current.getByteFrequencyData(dataArray);
      
      // Calculate volume for simple visualization
      let sum = 0;
      for (let i = 0; i < bufferLength; i++) {
        sum += dataArray[i];
      }
      const average = sum / bufferLength;
      setVolume(average);
      
      // Draw canvas
      ctx.fillStyle = '#0f0f10'; // BG color
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      const barWidth = (canvas.width / bufferLength) * 2.5;
      let barHeight;
      let x = 0;
      
      for (let i = 0; i < bufferLength; i++) {
        barHeight = dataArray[i] / 2;
        
        const gradient = ctx.createLinearGradient(0, canvas.height - barHeight, 0, canvas.height);
        gradient.addColorStop(0, '#FFD700'); // Gold
        gradient.addColorStop(1, '#B45309'); // Darker gold/orange
        
        ctx.fillStyle = gradient;
        ctx.fillRect(x, canvas.height - barHeight, barWidth, barHeight);
        
        x += barWidth + 1;
      }
    };
    
    draw();
  };

  if (!isPro) {
      return null; // Redirect logic in useEffect handles this
  }

  return (
    <div className="min-h-screen bg-dark-mk p-4 flex flex-col items-center justify-center relative overflow-hidden">
      {/* Background Pulse Effect when Active */}
      {isActive && (
        <div 
            className="absolute inset-0 z-0 pointer-events-none"
            style={{
                background: `radial-gradient(circle, rgba(255, 215, 0, ${Math.min(volume / 100, 0.3)}) 0%, rgba(15, 15, 16, 0) 70%)`
            }}
        />
      )}

      <div className="z-10 w-full max-w-md space-y-8 text-center">
        <div className="absolute top-4 left-4">
             <Button 
                variant="ghost" 
                onClick={() => navigate('/')}
                className="text-gray-400 hover:text-white"
              >
                <ArrowLeft className="h-6 w-6" />
              </Button>
        </div>

        <div>
            <h1 className="text-4xl font-bold text-gold mb-2">MK Voice</h1>
            <p className="text-gray-400">Convierte tu móvil en un micrófono para el Box</p>
        </div>

        <div className="flex justify-center my-12 relative">
            {/* Rings Animation */}
            {isActive && (
                <>
                    <div className="absolute inset-0 rounded-full border-4 border-gold opacity-20 animate-ping" style={{ animationDuration: '1.5s' }}></div>
                    <div className="absolute inset-0 rounded-full border-4 border-gold opacity-10 animate-ping" style={{ animationDuration: '2s', animationDelay: '0.5s' }}></div>
                </>
            )}
            
            <button
                onClick={toggleAudio}
                className={`
                    w-48 h-48 rounded-full flex items-center justify-center transition-all duration-300 shadow-2xl
                    ${isActive 
                        ? 'bg-gradient-to-br from-red-600 to-red-800 scale-105 shadow-[0_0_50px_rgba(220,38,38,0.5)]' 
                        : 'bg-gradient-to-br from-gray-800 to-gray-900 hover:scale-105 border-4 border-gray-700 hover:border-gold'
                    }
                `}
            >
                {isActive ? (
                    <MicOff className="w-20 h-20 text-white" />
                ) : (
                    <Mic className="w-20 h-20 text-gray-400" />
                )}
            </button>
        </div>

        <div className="h-32 w-full bg-gray-900/50 rounded-xl border border-gray-800 overflow-hidden flex items-center justify-center">
            {isActive ? (
                <canvas 
                    ref={canvasRef} 
                    width={400} 
                    height={128} 
                    className="w-full h-full"
                />
            ) : (
                <div className="text-gray-600 flex flex-col items-center">
                    <Volume2 className="w-8 h-8 mb-2 opacity-50" />
                    <p className="text-sm">Pulsa el botón para empezar</p>
                </div>
            )}
        </div>

        {error && (
            <div className="bg-red-900/30 border border-red-800 rounded-lg p-4 flex items-center gap-3 text-left">
                <AlertTriangle className="text-red-500 shrink-0" />
                <p className="text-red-200 text-sm">{error}</p>
            </div>
        )}

        <Card className="bg-yellow-900/20 border-yellow-800/50">
            <CardContent className="p-4">
                <p className="text-yellow-500 text-sm flex items-start gap-2 text-left">
                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                    <span>
                        <b>Atención:</b> Conecta tu móvil a un altavoz Bluetooth antes de activar. 
                        Mantén distancia para evitar acoples (pitidos).
                    </span>
                </p>
            </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default MKVoice;
